"use client";

import { Product } from "@prisma/client";
import React, { createContext, useContext, useEffect, useState } from "react";

interface CartItem extends Product {
  quantity: number;
  size: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: Product, quantity: number, size: string) => void;
  removeFromCart: (id: string, size: string) => void;
  updateCartItemQuantity: (id: string, size: string, quantity: number) => void;
  updateCartItemSize: (id: string, oldSize: string, newSize: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item: Product, quantity: number, size: string) => {
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (i) => i.id === item.id && i.size === size
      );
      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex] = {
          ...newCart[existingItemIndex],
          quantity: newCart[existingItemIndex].quantity + quantity,
        };
        return newCart;
      }
      return [...prevCart, { ...item, quantity, size }];
    });
  };

  const removeFromCart = (id: string, size: string) => {
    setCart((prevCart) =>
      prevCart.filter((item) => !(item.id === id && item.size === size))
    );
  };

  const updateCartItemQuantity = (
    id: string,
    size: string,
    quantity: number
  ) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === id && item.size === size ? { ...item, quantity } : item
      )
    );
  };

  const updateCartItemSize = (id: string, oldSize: string, newSize: string) => {
    setCart((prevCart) => {
      const itemToUpdate = prevCart.find(
        (item) => item.id === id && item.size === oldSize
      );
      if (!itemToUpdate) return prevCart;

      const existingItemWithNewSize = prevCart.find(
        (item) => item.id === id && item.size === newSize
      );

      if (existingItemWithNewSize) {
        // If an item with the new size already exists, merge quantities
        return prevCart
          .map((item) => {
            if (item.id === id && item.size === newSize) {
              return {
                ...item,
                quantity: item.quantity + itemToUpdate.quantity,
              };
            }
            return item.id === id && item.size === oldSize ? null : item;
          })
          .filter(Boolean) as CartItem[];
      } else {
        // If no item with the new size exists, just update the size
        return prevCart.map((item) =>
          item.id === id && item.size === oldSize
            ? { ...item, size: newSize }
            : item
        );
      }
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("cart");
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.quantity * item.price, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        updateCartItemSize,
        clearCart,
        getTotalItems,
        getTotalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
